import {ACCEPTED} from 'http-status-codes';
import _ from 'lodash';
import {ParsedQs} from 'qs';
import React, {useState, FunctionComponent, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import {Button, ButtonClass} from '@treez-ui/core/buttons';
import {ModalType} from '@treez-ui/core/feedback/modals';
import {ProductField, ProductInCatalog} from '@treez/commons/sharedTypings/product';

import {
    buildProductFiltersFromParams,
    ProductListView,
    searchProducts,
    SearchResultsAttributes,
    translateProductFiltersToParams,
    translateProductParamsToProductFilters,
} from '~/lib/product';
import {
    generateQueryString,
    getProductQueryParameters,
    getSearchQueryParameters,
    parseQueryString,
} from '~/lib/routing';
import {CurrentSelectionProvider} from '~/lib/selectedProductContext';
import {ProductFilters, ProductParameters, SearchQueryParameters} from '~/lib/sharedInterfaces';
import UIWrapper from '~/modules/pages/UIWrapper';
import {
    confirmAddingExistingBrandProducts,
    confirmProductsAdded,
    informUserOfErrorAddingProducts,
    informUserOfErrorFetchingProducts,
    informUserOfErrorLinking,
} from '~/redux/actionCreators/UserFeedbackCreators';
import {highlightMultipleProducts} from '~/redux/actions/HighlightedProductActions';
import {
    selectOrganizationCatalogId,
    selectStoresInOrganization,
} from '~/redux/selectors/organizationSelectors';
import {
    closeFeedbackModal,
    openFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';

import {
    addProductsToCatalog,
    getCatalogProductData,
    linkToBrandProduct,
    ProductStorePresenceAttributes,
    SearchFilter,
    searchExistingLinks,
} from '~/utils/ProductAPI';
import BrandTreezNavTools from './BrandTreezNavTools';
import BrandProducts from './BrandProducts';
import BrandProductModal from './BrandProductModal';
import BrandTreezActions from './BrandTreezActions';
import LinkProductHeader from './LinkProductHeader';
import {StoreAttributes} from '~/lib/directory';
import {getStoreProductValidationResult, StoreProductValidationResult} from '~/lib/errors';
import ProductValidationErrorMessage from '~/modules/widgets/products/ProductValidationErrorMessage';

const {TILE, TABLE} = ProductListView;

export interface BrandTreezPageParameters
    extends ParsedQs,
        Omit<SearchQueryParameters, 'page' | 'pageSize'>,
        ProductParameters {
    page: string;
    pageSize: string;
    view: ProductListView;
    productToLink?: string;
}

export const BrandTreezPage: FunctionComponent<RouteComponentProps> = (
    props: RouteComponentProps
) => {
    const {history, location} = props;

    // remove tokens info from url, if exists
    const params = new URLSearchParams(location.search);
    if (params.get('tokens') !== null) {
        params.delete('tokens');
        history.replace({
            search: params.toString(),
        });
    }

    const queryParams = parseQueryString(location.search);
    const searchParams = getSearchQueryParameters(queryParams);
    const productParams = getProductQueryParameters(queryParams);

    const {view} = queryParams;

    const {page, pageSize, searchTerm, sortOrder, sortCategory} = searchParams;

    const organizationCatalogId = useSelector(selectOrganizationCatalogId);
    const stores = useSelector(selectStoresInOrganization) || [];

    // Link Product
    const [productToLink, setProductToLink] = useState<ProductInCatalog>();
    const [productToLinkStorePresence, setProductToLinkStorePresence] =
        useState<ProductStorePresenceAttributes[]>();

    // Brand Products
    const [products, setProducts] = useState<ProductInCatalog[]>([]);
    const [productCount, setProductCount] = useState<number>(0);
    const [fetching, setFetching] = useState<boolean>(false);
    const [productBeingInspected, setProductBeingInspected] = useState<
        ProductInCatalog | undefined
    >();
    const [filters, setFilters] = useState<SearchFilter[]>([]);

    const dispatch = useDispatch();

    const receiveProducts = (response: SearchResultsAttributes) => {
        const {data, total} = response;

        setProducts(data);
        setProductCount(total);
        setFetching(false);
    };

    const receiveBrandMatchesForLinking = async (response: SearchResultsAttributes) => {
        const {total} = response;

        // If you receive results, just receive them
        if (total > 0) {
            receiveProducts(response);
        }
        // else remove the brand filter and put it into the search term if there is one
        else {
            const brandFilter = _.find(filters, (filter) => {
                return filter.field === ProductField.BRAND_NAME;
            });

            if (brandFilter) {
                const brandFilterValue = _.head(brandFilter.values as string[]) ?? '';
                const productParamsWithoutBrand = _.omit(productParams, 'brand');

                history.replace({
                    search: generateQueryString({
                        ...searchParams,
                        ...productParamsWithoutBrand,
                        searchTerm: brandFilterValue,
                    }),
                });

                setFetching(false);
            } else {
                receiveProducts(response);
            }
        }
    };

    const fetchProductToLinkStorePresence = (
        productToLink: ProductInCatalog,
        stores: StoreAttributes[]
    ) => {
        const storeCatalogIds = stores
            .filter((store) => Boolean(store.catalogId))
            .map((store) => store.catalogId) as number[];

        getCatalogProductData(storeCatalogIds, [productToLink.id]).then((result) => {
            if (result[productToLink.id]) {
                setProductToLinkStorePresence(result[productToLink.id]);
            }
        });
    };

    const handleErrorFetchingProducts = (error: Error) => {
        console.error(error);
        setFetching(false);
        setProducts([]);
        dispatch(informUserOfErrorFetchingProducts(error.message as string));
    };

    const updateSearchTerm = (searchTerm: string) => {
        history.replace({
            search: generateQueryString({
                ...queryParams,
                searchTerm,
            }),
        });
    };

    const changeView = (view: ProductListView) => {
        history.replace({
            search: generateQueryString({
                ...queryParams,
                view,
            }),
        });
    };

    const applyFilters = (filtersApplied: ProductFilters) => {
        history.replace({
            search: generateQueryString({
                ...searchParams,
                ...translateProductFiltersToParams(filtersApplied),
            }),
        });
    };

    const confirmProductLinked = () => {
        openFeedbackModal(closeFeedbackModal, 'Product Linked', {
            bodyText: 'Product has been linked to BrandTreez',
            type: ModalType.INFORMATION,
        });
        history.goBack();
    };

    // Life Cycle Methods
    const baseParametersAreMissing = pageSize == null || page == null || view == null;

    const setBaseParameters = () => {
        history.replace({
            search: generateQueryString({
                ...queryParams,
                page: queryParams.page ?? 1,
                pageSize: queryParams.view === TABLE ? 50 : 32,
                view: queryParams.view ?? TILE,
            }),
        });
    };

    useEffect(() => {
        if (baseParametersAreMissing) {
            setBaseParameters();
        }

        const productToLinkInNav = _.get(location, 'state.productToLink');

        if (productToLinkInNav) {
            setProductToLink(productToLinkInNav);

            if (stores.length > 0) {
                fetchProductToLinkStorePresence(productToLinkInNav, stores);
            }
        }
    }, []);

    // If Search Parameters Change
    useEffect(() => {
        if (baseParametersAreMissing) {
            setBaseParameters();
        } else {
            setFetching(true);
        }
    }, [filters, page, pageSize, searchTerm, sortCategory, sortOrder]);

    useEffect(() => {
        if (fetching === false) {
        } else {
            const productNavigation = {
                ...searchParams,
                filters,
            };

            if (productToLink == null) {
                searchProducts(productNavigation)
                    .then(receiveProducts)
                    .catch(handleErrorFetchingProducts);
            } else {
                searchProducts(productNavigation)
                    .then(receiveBrandMatchesForLinking)
                    .catch(handleErrorFetchingProducts);
            }
        }
    }, [fetching]);

    useEffect(() => {
        const productFilters = buildProductFiltersFromParams(productParams);
        setFilters(productFilters);
    }, [location.search]);

    // API Calls
    const addProducts = async (productsToAdd: Partial<ProductInCatalog>[]) => {
        try {
            const productIds = _.map(productsToAdd, 'id') as number[];

            const productsAdded = await addProductsToCatalog(organizationCatalogId, productIds);

            dispatch(highlightMultipleProducts(productsAdded));

            dispatch(confirmProductsAdded(productsAdded.length));
        } catch (error) {
            console.error(error);
            dispatch(
                informUserOfErrorAddingProducts(error.message as string, productsToAdd.length)
            );
        }
    };

    const linkProduct = async (brandProduct: ProductInCatalog) => {
        const catalogIdsThatCarryProduct = (productToLinkStorePresence || [])
            .filter((presence) => presence.status === 'ACTIVE')
            .map((presence) => presence.id);

        const storeProductValidationErrors: StoreProductValidationResult[] =
            getStoreProductValidationResult(
                brandProduct,
                catalogIdsThatCarryProduct,
                stores,
                dispatch
            );

        if (storeProductValidationErrors.length > 0) {
            handleOpenProductValidationFeedback(storeProductValidationErrors);
            return;
        }

        try {
            if (productToLink) {
                const response = await linkToBrandProduct(productToLink.id, brandProduct.id);

                if (response.status === ACCEPTED) {
                    confirmProductLinked();
                } else {
                    dispatch(informUserOfErrorLinking());
                }
            }
        } catch (error) {
            console.error(error);
            dispatch(informUserOfErrorLinking());
        }
    };

    const handleOpenProductValidationFeedback = (
        storeProductValidationErrors: StoreProductValidationResult[]
    ) => {
        openFeedbackModal(closeFeedbackModal, `Not All Product Requirements Are Met`, {
            type: ModalType.ERROR,
            bodyElement: (
                <>
                    {storeProductValidationErrors.map((validationResult, key) => (
                        <ProductValidationErrorMessage
                            key={key}
                            productValidationResult={validationResult.productValidationResult}
                            region={validationResult.region}
                            store={validationResult.store}
                        />
                    ))}
                </>
            ),
        });
    };

    const handleAddProducts = async (products: Partial<ProductInCatalog>[]) => {
        try {
            if (organizationCatalogId == null) {
                console.error('No Organization Catalog Identified to Add To');
                throw new Error('Could not find Central Catalog');
            }

            const productIds = _.map(products, 'id') as number[];

            const existingLinks = await searchExistingLinks(organizationCatalogId, productIds);

            if (_.isEmpty(existingLinks)) {
                await addProducts(products);
            } else {
                const numOfDuplicates = _.uniqBy(existingLinks, 'linkedTo').length;

                dispatch(
                    confirmAddingExistingBrandProducts(
                        () => addProducts(products),
                        numOfDuplicates,
                        productIds.length
                    )
                );
            }
        } catch (error) {
            console.error(error);
            dispatch(informUserOfErrorAddingProducts(error.message as string, products.length));
        }
    };

    const renderProductModalButton = () => {
        if (productBeingInspected == null) {
            // This won't happen as the modal will not be open without a product
            return <div />;
        }
        if (productToLink == null) {
            return (
                <div className="button-container-large">
                    <Button
                        buttonClass={ButtonClass.PREFERRED}
                        onClick={() => handleAddProducts([productBeingInspected])}
                        text="Add Product"
                    />
                </div>
            );
        }
        return (
            <div className="button-container-large">
                <Button
                    text="Link Product"
                    buttonClass={ButtonClass.PREFERRED}
                    onClick={() => linkProduct(productBeingInspected)}
                />
            </div>
        );
    };

    return (
        <UIWrapper>
            <div className="brand-treez-page">
                {productToLink && <LinkProductHeader productToLink={productToLink} />}
                <BrandTreezNavTools
                    currentView={view as ProductListView}
                    filters={translateProductParamsToProductFilters(productParams)}
                    searchTerm={searchTerm || ''}
                    applyFilters={applyFilters}
                    changeView={changeView}
                    updateSearchTerm={updateSearchTerm}
                />
                <CurrentSelectionProvider>
                    <BrandProducts
                        values={products}
                        productCount={productCount}
                        linkProduct={linkProduct}
                        inspectProduct={(productToInspect: ProductInCatalog) =>
                            setProductBeingInspected(productToInspect)
                        }
                        productToLink={productToLink}
                    />
                    <BrandTreezActions handleAddProducts={handleAddProducts} />
                </CurrentSelectionProvider>
            </div>
            <BrandProductModal
                callToAction={renderProductModalButton()}
                closeProductDetails={() => setProductBeingInspected(undefined)}
                product={productBeingInspected}
            />
        </UIWrapper>
    );
};

export default BrandTreezPage;
